import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import Dashboard from "@/pages/Dashboard";
import UploadPage from "@/pages/UploadPage";
import StagingTablePage from "@/pages/StagingTablePage";
import TransformPage from "@/pages/TransformPage";
import ApiDefinitionPage from "@/pages/ApiDefinitionPage";
import ExecutionConsolePage from "@/pages/ExecutionConsolePage";
import ExecutionListPage from "@/pages/ExecutionListPage";
import TemplateLibraryPage from "@/pages/TemplateLibraryPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import LookupTablesPage from "@/pages/LookupTablesPage";
import CompanyManagementPage from "@/pages/CompanyManagementPage";
import UserManagementPage from "@/pages/UserManagementPage";
import MailboxMonitorPage from "@/pages/MailboxMonitorPage";
import AuditLogPage from "@/pages/AuditLogPage";
import LandingPage from "@/pages/LandingPage";
import GlobalResourcesPage from "@/pages/GlobalResourcesPage";
import ProfilePage from "@/pages/ProfilePage";
import PowerSuiteHubPage from "@/pages/PowerSuiteHubPage";
import ScheduledRunsPage from "@/pages/ScheduledRunsPage";
import WebhookTriggersPage from "@/pages/WebhookTriggersPage";
import AnalyticsDashboardPage from "@/pages/AnalyticsDashboardPage";
import DataProfilerPage from "@/pages/DataProfilerPage";
import VariablesStorePage from "@/pages/VariablesStorePage";
import CustomFunctionsPage from "@/pages/CustomFunctionsPage";
import ValidationRulesPage from "@/pages/ValidationRulesPage";
import OpenAPIImportPage from "@/pages/OpenAPIImportPage";
import MultiFileMergePage from "@/pages/MultiFileMergePage";
import ExportImportPage from "@/pages/ExportImportPage";
import ExecutionSequencesPage from "@/pages/ExecutionSequencesPage";
import PricingPlansPage from "@/pages/PricingPlansPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";

import ExecutionDiffPage from "@/pages/ExecutionDiffPage";

const wrap = (Page, adminOnly = false, superAdminOnly = false) => (
  <ProtectedRoute adminOnly={adminOnly} superAdminOnly={superAdminOnly}>
    <Layout><Page /></Layout>
  </ProtectedRoute>
);

function SmartRoot() {
  const { user } = useAuth();
  if (user === undefined) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
      <div className="w-8 h-8 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <LandingPage />;
  if (user.force_password_reset) return <Navigate to="/change-password" replace />;
  return <Layout><Dashboard /></Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/change-password" element={<ProtectedRoute><Layout><ChangePasswordPage /></Layout></ProtectedRoute>} />
      <Route path="/" element={<SmartRoot />} />
      <Route path="/upload" element={wrap(UploadPage)} />
      <Route path="/staging/:uploadId" element={wrap(StagingTablePage)} />
      <Route path="/transform/:uploadId" element={wrap(TransformPage)} />
      <Route path="/definitions" element={wrap(ApiDefinitionPage)} />
      <Route path="/executions" element={wrap(ExecutionListPage)} />
      <Route path="/executions/:executionId" element={wrap(ExecutionConsolePage)} />
      <Route path="/execute/:uploadId" element={wrap(ExecutionConsolePage)} />
      <Route path="/execute" element={wrap(ExecutionConsolePage)} />
      <Route path="/templates" element={wrap(TemplateLibraryPage)} />
      <Route path="/lookups" element={wrap(LookupTablesPage)} />
      <Route path="/global-resources" element={wrap(GlobalResourcesPage)} />
      <Route path="/admin" element={wrap(AdminSettingsPage, true)} />
      <Route path="/admin/companies" element={wrap(CompanyManagementPage, false, true)} />
      <Route path="/admin/users" element={wrap(UserManagementPage, true)} />
      <Route path="/admin/mailboxes" element={wrap(MailboxMonitorPage, true)} />
      <Route path="/admin/audit" element={wrap(AuditLogPage, true)} />
      <Route path="/profile" element={<ProtectedRoute><Layout><ProfilePage /></Layout></ProtectedRoute>} />
      <Route path="/power" element={wrap(PowerSuiteHubPage)} />
      <Route path="/power/schedules" element={wrap(ScheduledRunsPage)} />
      <Route path="/power/webhooks" element={wrap(WebhookTriggersPage)} />
      <Route path="/power/analytics" element={wrap(AnalyticsDashboardPage)} />
      <Route path="/power/profiler" element={wrap(DataProfilerPage)} />
      <Route path="/power/variables" element={wrap(VariablesStorePage)} />
      <Route path="/power/functions" element={wrap(CustomFunctionsPage)} />
      <Route path="/power/validation" element={wrap(ValidationRulesPage)} />
      <Route path="/power/openapi" element={wrap(OpenAPIImportPage)} />
      <Route path="/power/merge" element={wrap(MultiFileMergePage)} />
      <Route path="/power/export-import" element={wrap(ExportImportPage)} />
      <Route path="/power/sequences" element={wrap(ExecutionSequencesPage)} />
      <Route path="/executions/diff" element={wrap(ExecutionDiffPage)} />
      <Route path="/admin/pricing" element={wrap(PricingPlansPage, false, true)} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
